import React, { useState, useEffect, useCallback } from 'react';
import { clsx } from 'clsx';
import {
  MapIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import planService, {
  Plan,
  PlanRectangle,
  CreatePlanDto,
  UpdatePlanDto,
  CreateRectangleDto,
  UpdateRectangleDto,
} from '../../services/planService';
import { Button, Input, ConfirmModal, useToast, PageHero } from '../ui';
import PlansList from './PlansList';
import MindMapCanvas from './MindMapCanvas';
import PlanFormModal from './PlanFormModal';
import RectangleFormModal from './RectangleFormModal';
import RectangleDetailModal from './RectangleDetailModal';

const PlansPage: React.FC = () => {
  const toast = useToast();

  // Plans state
  const [plans, setPlans] = useState<Plan[]>([]);
  const [filteredPlans, setFilteredPlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingPlans, setLoadingPlans] = useState(true);

  // Rectangles state
  const [rectangles, setRectangles] = useState<PlanRectangle[]>([]);
  const [selectedRectangle, setSelectedRectangle] = useState<PlanRectangle | null>(null);
  const [loadingRectangles, setLoadingRectangles] = useState(false);

  // Modals
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [showRectangleModal, setShowRectangleModal] = useState(false);
  const [editingRectangle, setEditingRectangle] = useState<PlanRectangle | null>(null);
  const [parentIdForNewRectangle, setParentIdForNewRectangle] = useState<number | null>(null);
  const [deletingPlan, setDeletingPlan] = useState<Plan | null>(null);
  const [deletingRectangle, setDeletingRectangle] = useState<PlanRectangle | null>(null);
  const [viewingRectangle, setViewingRectangle] = useState<PlanRectangle | null>(null);

  // Load plans
  const loadPlans = useCallback(async () => {
    try {
      setLoadingPlans(true);
      const data = await planService.getPlans();
      setPlans(data);
      setFilteredPlans(data);
    } catch (error) {
      console.error('Error loading plans:', error);
      toast.error('Error', 'Failed to load plans');
    } finally {
      setLoadingPlans(false);
    }
  }, [toast]);

  // Load rectangles for selected plan
  const loadRectangles = useCallback(async (planId: number) => {
    try {
      setLoadingRectangles(true);
      const data = await planService.getRectangles(planId);
      setRectangles(data);
    } catch (error) {
      console.error('Error loading rectangles:', error);
      toast.error('Error', 'Failed to load plan nodes');
    } finally {
      setLoadingRectangles(false);
    }
  }, [toast]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    if (selectedPlan) {
      loadRectangles(selectedPlan.id);
    } else {
      setRectangles([]);
    }
  }, [selectedPlan, loadRectangles]);

  // Filter plans by search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredPlans(plans);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredPlans(
        plans.filter(
          (p) =>
            p.name.toLowerCase().includes(query) ||
            p.description?.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, plans]);

  // Plan handlers
  const handleCreatePlan = async (data: CreatePlanDto) => {
    const newPlan = await planService.createPlan(data);
    setPlans((prev) => [newPlan, ...prev]);
    setSelectedPlan(newPlan);
    toast.success('Success', 'Plan created successfully');
  };

  const handleUpdatePlan = async (data: UpdatePlanDto) => {
    if (!editingPlan) return;
    const updated = await planService.updatePlan(editingPlan.id, data);
    setPlans((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    if (selectedPlan?.id === updated.id) {
      setSelectedPlan(updated);
    }
    toast.success('Success', 'Plan updated successfully');
  };

  const handleDeletePlan = async () => {
    if (!deletingPlan) return;
    await planService.deletePlan(deletingPlan.id);
    setPlans((prev) => prev.filter((p) => p.id !== deletingPlan.id));
    if (selectedPlan?.id === deletingPlan.id) {
      setSelectedPlan(null);
    }
    setDeletingPlan(null);
    toast.success('Success', 'Plan deleted successfully');
  };

  // Rectangle handlers
  const handleCreateRectangle = async (data: CreateRectangleDto) => {
    if (!selectedPlan) return;
    const newRect = await planService.createRectangle(selectedPlan.id, data);
    setRectangles((prev) => [...prev, newRect]);
    toast.success('Success', 'Node added successfully');
  };

  const handleUpdateRectangle = async (data: UpdateRectangleDto) => {
    if (!selectedPlan || !editingRectangle) return;
    const updated = await planService.updateRectangle(
      selectedPlan.id,
      editingRectangle.id,
      data
    );
    setRectangles((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    toast.success('Success', 'Node updated successfully');
  };

  const handleDeleteRectangle = async () => {
    if (!selectedPlan || !deletingRectangle) return;
    await planService.deleteRectangle(selectedPlan.id, deletingRectangle.id);
    setRectangles((prev) => prev.filter((r) => r.id !== deletingRectangle.id));
    setDeletingRectangle(null);
    toast.success('Success', 'Node deleted successfully');
  };

  // Handle position update for mind map drag
  const handleUpdateRectanglePosition = async (id: number, data: UpdateRectangleDto) => {
    if (!selectedPlan) return;
    try {
      const updated = await planService.updateRectangle(selectedPlan.id, id, data);
      setRectangles((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch (error) {
      toast.error('Error', 'Failed to update node position');
    }
  };

  // Handle updating linked users/tasks from detail modal
  const handleUpdateRectangleLinks = async (data: UpdateRectangleDto) => {
    if (!selectedPlan || !viewingRectangle) return;
    const updated = await planService.updateRectangle(
      selectedPlan.id,
      viewingRectangle.id,
      data
    );
    setRectangles((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setViewingRectangle(null);
    toast.success('Success', 'Links updated successfully');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50/30">
      {/* Enhanced Hero Section */}
      <div className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <PageHero
              title="Plans"
              subtitle="Visual tree-based planning for your projects"
              icon={<MapIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
              accentColor="cyan"
            >
              <div className="mt-2">
                <Button
                  variant="primary"
                  onClick={() => {
                    setEditingPlan(null);
                    setShowPlanModal(true);
                  }}
                  leftIcon={<PlusIcon className="h-5 w-5" />}
                >
                  New Plan
                </Button>
              </div>
            </PageHero>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Sidebar - Plans List */}
          <div className="lg:col-span-4 xl:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search plans..."
                    className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="p-4 max-h-[calc(100vh-320px)] overflow-y-auto">
                <PlansList
                  plans={filteredPlans}
                  selectedPlanId={selectedPlan?.id || null}
                  onSelectPlan={(plan) => setSelectedPlan(plan)}
                  onCreatePlan={() => {
                    setEditingPlan(null);
                    setShowPlanModal(true);
                  }}
                  onEditPlan={(plan) => {
                    setEditingPlan(plan);
                    setShowPlanModal(true);
                  }}
                  onDeletePlan={(plan) => setDeletingPlan(plan)}
                  loading={loadingPlans}
                />
              </div>
            </div>
          </div>

          {/* Right - Canvas */}
          <div className="lg:col-span-8 xl:col-span-9">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 200px)', minHeight: '600px' }}>
              {selectedPlan ? (
                <>
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white flex-shrink-0">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {selectedPlan.name}
                    </h2>
                    {selectedPlan.description && (
                      <p className="text-sm text-gray-500 mt-1">
                        {selectedPlan.description}
                      </p>
                    )}
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <MindMapCanvas
                      rectangles={rectangles}
                      selectedRectangleId={selectedRectangle?.id || null}
                      onSelectRectangle={setSelectedRectangle}
                      onAddRectangle={(parentId) => {
                        setEditingRectangle(null);
                        setParentIdForNewRectangle(parentId || null);
                        setShowRectangleModal(true);
                      }}
                      onEditRectangle={(rect) => {
                        setEditingRectangle(rect);
                        setParentIdForNewRectangle(null);
                        setShowRectangleModal(true);
                      }}
                      onDeleteRectangle={(rect) => setDeletingRectangle(rect)}
                      onViewRectangle={(rect) => setViewingRectangle(rect)}
                      onUpdateRectanglePosition={handleUpdateRectanglePosition}
                      loading={loadingRectangles}
                    />
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-full py-24">
                  <div className="text-center">
                    <MapIcon className="mx-auto h-16 w-16 text-gray-300" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">
                      Select a plan
                    </h3>
                    <p className="mt-2 text-sm text-gray-500">
                      Choose a plan from the list or create a new one
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Plan Form Modal */}
      {showPlanModal && (
        <PlanFormModal
          plan={editingPlan}
          onClose={() => {
            setShowPlanModal(false);
            setEditingPlan(null);
          }}
          onSubmit={editingPlan ? handleUpdatePlan : handleCreatePlan}
        />
      )}

      {/* Rectangle Form Modal */}
      {showRectangleModal && (
        <RectangleFormModal
          rectangle={editingRectangle}
          parentId={parentIdForNewRectangle}
          onClose={() => {
            setShowRectangleModal(false);
            setEditingRectangle(null);
            setParentIdForNewRectangle(null);
          }}
          onSubmit={editingRectangle ? handleUpdateRectangle : handleCreateRectangle}
        />
      )}

      {/* Delete Plan Confirmation */}
      {deletingPlan && (
        <ConfirmModal
          isOpen={true}
          title="Delete Plan"
          message={`Are you sure you want to delete "${deletingPlan.name}"? This will also delete all nodes in the plan. This action cannot be undone.`}
          confirmText="Delete"
          variant="danger"
          onConfirm={handleDeletePlan}
          onClose={() => setDeletingPlan(null)}
        />
      )}

      {/* Delete Rectangle Confirmation */}
      {deletingRectangle && (
        <ConfirmModal
          isOpen={true}
          title="Delete Node"
          message={`Are you sure you want to delete "${deletingRectangle.title}"? This action cannot be undone.`}
          confirmText="Delete"
          variant="danger"
          onConfirm={handleDeleteRectangle}
          onClose={() => setDeletingRectangle(null)}
        />
      )}

      {/* Rectangle Detail Modal */}
      {viewingRectangle && (
        <RectangleDetailModal
          rectangle={viewingRectangle}
          onClose={() => setViewingRectangle(null)}
          onUpdate={handleUpdateRectangleLinks}
        />
      )}
    </div>
  );
};

export default PlansPage;

